import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = 'https://tribaltech-backend.onrender.com';

function dollars(cents) { return '$' + ((cents || 0) / 100).toFixed(2); }

export default function AffiliateDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [connect, setConnect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');
  const [copied, setCopied] = useState(false);

  const token = localStorage.getItem('affiliate_token');
  const headers = { Authorization: 'Bearer ' + token };

  useEffect(() => {
    if (!token) { navigate('/affiliate/login', { replace: true }); return; }
    Promise.all([
      axios.get(`${API}/api/affiliate/me`, { headers }),
      axios.get(`${API}/api/affiliate/connect/status`, { headers }).catch(() => ({ data: null })),
    ]).then(([me, cs]) => {
      setData(me.data);
      setConnect(cs.data);
    }).catch(e => {
      if (e.response?.status === 401) {
        localStorage.removeItem('affiliate_token');
        navigate('/affiliate/login', { replace: true });
        return;
      }
      setErr(e.response?.data?.error || e.message);
    }).finally(() => setLoading(false));
  }, [token, navigate]);

  async function startOnboarding() {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/affiliate/connect/onboarding-link`, {}, { headers });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      alert(e.response?.data?.error || e.message);
      setBusy(false);
    }
  }

  function signOut() {
    localStorage.removeItem('affiliate_token');
    localStorage.removeItem('affiliate_email');
    localStorage.removeItem('affiliate_code');
    navigate('/affiliate/login', { replace: true });
  }

  function copyCode() {
    const code = data?.affiliate?.code;
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  if (loading) return <div style={{ ...page, color:'#888', padding: 40 }}>Loading…</div>;
  if (err) return <div style={{ ...page, color:'#f87171', padding: 40 }}>{err}</div>;

  const aff = data.affiliate;
  const stats = data.stats;

  return (
    <div style={page}>
      <div style={{ maxWidth: 1100, margin:'0 auto', padding:'40px 20px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 30 }}>
          <div>
            <div style={eyebrow}>Creator Protect · Affiliate</div>
            <h1 style={{ color:'#fff', fontSize: 28, margin:'4px 0 2px' }}>Your dashboard</h1>
            <div style={{ color:'#94a3b8', fontSize: 13 }}>{aff.email}</div>
          </div>
          <button onClick={signOut} style={signOutBtn}>Sign out</button>
        </div>

        <div style={grid4}>
          <StatCard label="Clicks"   value={stats.clicks}  color="#a78bfa" />
          <StatCard label="Sales"    value={stats.sales}   color="#4ade80" />
          <StatCard label="Earnings" value={dollars(stats.earnings_cents)} color="#fbbf24" />
          <StatCard label="Pending payout" value={dollars(stats.pending_cents)} color="#60a5fa" />
        </div>

        <div style={{ ...panel, marginTop: 24 }}>
          <div style={{ color:'#fff', fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Your referral code</div>
          <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
            <code style={codeBox}>{aff.code}</code>
            <button onClick={copyCode} style={copyBtn}>{copied ? 'Copied' : 'Copy'}</button>
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color:'#94a3b8' }}>
            Share a creator's storefront with <code>?ref={aff.code}</code>. Example:
          </div>
          <div style={{ marginTop: 6, fontFamily:'monospace', fontSize: 12, color:'#a78bfa', wordBreak:'break-all' }}>
            {data.share_url_example}
          </div>
        </div>

        <div style={{ ...panel, marginTop: 20 }}>
          <div style={{ color:'#fff', fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Payouts</div>
          {!connect?.connected ? (
            <>
              <div style={{ color:'#94a3b8', fontSize: 13, marginBottom: 12 }}>
                Connect a bank via Stripe so we can send you your earnings. Stripe handles all KYC and deposits.
              </div>
              <button onClick={startOnboarding} disabled={busy} style={primaryBtn}>
                {busy ? '…' : 'Connect bank'}
              </button>
            </>
          ) : !connect.charges_enabled ? (
            <>
              <div style={{ color:'#fbbf24', fontSize: 13, marginBottom: 12 }}>
                Stripe still needs a few details before we can pay you.
              </div>
              <button onClick={startOnboarding} disabled={busy} style={primaryBtn}>
                {busy ? '…' : 'Finish Stripe setup'}
              </button>
            </>
          ) : (
            <div style={{ color:'#4ade80', fontSize: 13 }}>
              ✓ Connected · Payouts {connect.payouts_enabled ? 'enabled' : 'pending'}
            </div>
          )}
        </div>

        <div style={{ ...panel, marginTop: 20 }}>
          <div style={{ color:'#fff', fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Recent sales</div>
          {data.recent_sales?.length ? (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color:'#94a3b8', textAlign:'left', borderBottom:'1px solid #222' }}>
                  <th style={th}>Date</th>
                  <th style={th}>Sale</th>
                  <th style={th}>Your cut</th>
                  <th style={th}>Paid out?</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_sales.map(s => (
                  <tr key={s.id} style={{ color:'#fff', borderBottom:'1px solid #1a1a1a' }}>
                    <td style={td}>{new Date(s.purchase_timestamp).toLocaleDateString()}</td>
                    <td style={td}>{dollars(s.amount_cents)}</td>
                    <td style={td}>{dollars(s.affiliate_commission_cents)}</td>
                    <td style={td}>{s.affiliate_transfer_id ? '✓' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ color:'#666', fontSize: 13 }}>
              No sales yet. Share your link — earnings roll in automatically when someone buys.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ ...panel, padding: 20 }}>
      <div style={{ fontSize: 11, color:'#94a3b8', textTransform:'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 600, color, marginTop: 6 }}>{value}</div>
    </div>
  );
}

const page = { minHeight:'100vh', background:'linear-gradient(180deg, #0f0f1e 0%, #0a0a0f 100%)' };
const eyebrow = { fontSize: 11, color:'#888', letterSpacing: 2, textTransform:'uppercase' };
const grid4 = { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap: 16 };
const panel  = { background:'#111', borderRadius: 12, border:'1px solid #222', padding: 20 };
const codeBox= { background:'#0a0a0a', padding:'8px 14px', borderRadius: 6, border:'1px solid #222',
                 color:'#a78bfa', fontFamily:'monospace', fontSize: 18, letterSpacing: 2 };
const copyBtn= { padding:'8px 14px', borderRadius: 6, border:'1px solid #333', background:'transparent',
                 color:'#94a3b8', fontSize: 12, cursor:'pointer' };
const primaryBtn = { padding:'10px 18px', borderRadius: 8, border:'none', background:'#7c3aed',
                     color:'#fff', fontSize: 13, fontWeight: 600, cursor:'pointer' };
const signOutBtn = { padding:'8px 14px', borderRadius: 8, border:'1px solid #333', background:'transparent',
                     color:'#94a3b8', fontSize: 13, cursor:'pointer' };
const th = { padding:'8px 6px', fontWeight: 500 };
const td = { padding:'10px 6px' };
